import React from 'react';
import { View, Text, TextInput, TouchableOpacity, Platform } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

export type MetaRowProps = {
  label: string;
  value: string | string[];
  editMode?: boolean;
  onChange?: (next: string | string[]) => void;
  onChangeLabel?: (nextLabel: string) => void;
  onDelete?: () => void;
  // Mark this row as a website row: show platform icon-only in view, input in edit
  isWebsite?: boolean;
  // Mark this row as a star rating row
  isStarRating?: boolean;
  // Given a URL, return { Icon, color }
  pickSocialIcon?: (url: string) => { Icon: React.ComponentType<any>; color: string; label?: string };
  // Called to open a link
  openUrl?: (url: string) => void;
  // Placeholder text to show when value is empty in view mode
  placeholderText?: string;
};

const MetaRow: React.FC<MetaRowProps> = ({
  label,
  value,
  editMode = false,
  onChange,
  onChangeLabel,
  onDelete,
  isWebsite = false,
  isStarRating = false,
  pickSocialIcon,
  openUrl,
  placeholderText = '未提供',
}) => {
  if (isWebsite) {
    const ensureArray = (v: string | string[] | undefined | null): string[] => {
      if (Array.isArray(v)) return v;
      const raw = String(v || '').trim();
      return raw ? [raw] : [];
    };
    const links = ensureArray(value);

    if (editMode) {
      return (
        <View style={{ paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
          <Text style={{ color: '#6B7280', fontSize: 14, marginBottom: 6 }}>{label}</Text>
          {links.length === 0 ? (
            <TouchableOpacity onPress={() => onChange && onChange([''])} style={{ paddingVertical: 8 }}>
              <Text style={{ color: '#0B61D6', fontSize: 14, fontWeight: '700' }}>+ 添加链接</Text>
            </TouchableOpacity>
          ) : (
            <>
              {links.map((lk, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <TextInput
                    value={lk}
                    onChangeText={(txt) => {
                      if (!onChange) return;
                      const next = links.slice();
                      next[i] = txt;
                      onChange(next);
                    }}
                    placeholder={'粘贴网址或账号链接'}
                    autoCapitalize={'none'}
                    style={{ flex: 1, borderWidth: 0, borderColor: 'transparent', borderRadius: 0, paddingHorizontal: 0, paddingVertical: Platform.OS === 'web' ? 6 : 4, color: '#111827' }}
                  />
                  <TouchableOpacity onPress={() => onChange && onChange(links.filter((_, idx) => idx !== i))}>
                    <Text style={{ color: '#EF4444', fontSize: 13 }}>删除</Text>
                  </TouchableOpacity>
                </View>
              ))}
              <TouchableOpacity onPress={() => onChange && onChange([...links, ''])} style={{ paddingVertical: 4 }}>
                <Text style={{ color: '#0B61D6', fontSize: 14, fontWeight: '700' }}>+ 添加链接</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      );
    }

    // view mode: show icons for each link; clicking opens URL
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
        <Text style={{ width: '30%', color: '#6B7280', fontSize: 14 }}>{label}</Text>
        {links.length > 0 ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', width: '70%' }}>
            {links.map((raw, idx) => {
              const href = raw ? (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`) : '';
              const iconPack = pickSocialIcon && pickSocialIcon(href || '');
              const Icon = iconPack?.Icon as any;
              const color = iconPack?.color || '#0B61D6';
              return (
                <TouchableOpacity key={idx} onPress={() => { if (openUrl && href) openUrl(href); }}>
                  {Icon ? <Icon style={{ color, fontSize: 20 }} /> : <Text style={{ color: '#0B61D6', fontSize: 14, fontWeight: '700' }}>Link</Text>}
                </TouchableOpacity>
              );
            })}
          </View>
        ) : (
          <Text style={{ width: '70%', color: '#9CA3AF', fontSize: 14 }}>{placeholderText}</Text>
        )}
      </View>
    );
  }

  // Default meta row
  const hasValue = Array.isArray(value) ? value.filter(v => String(v || '').trim().length > 0).length > 0 : String(value || '').trim().length > 0;
  
  const renderStars = (val: string | string[]) => {
    const strVal = Array.isArray(val) ? val[0] : val;
    const numVal = parseFloat(strVal);
    
    if (isNaN(numVal)) {
      return <Text style={{ color: '#111827', fontSize: 14, fontWeight: '700' }}>{strVal}</Text>;
    }

    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (numVal >= i) {
        stars.push(<PlatformIcon key={i} name="star" size={18} color="#F59E0B" />);
      } else if (numVal >= i - 0.5) {
        stars.push(<PlatformIcon key={i} name="star-half" size={18} color="#F59E0B" />);
      } else {
        stars.push(<PlatformIcon key={i} name="star-outline" size={18} color="#D1D5DB" />);
      }
    }
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View style={{ flexDirection: 'row' }}>{stars}</View>
      </View>
    );
  };

  return (
    <View style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
      {editMode ? (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <TextInput
              value={label}
              onChangeText={(text) => onChangeLabel && onChangeLabel(text)}
              placeholder={'欄位名稱'}
              style={{ width: 120, borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 4, paddingHorizontal: 8, paddingVertical: Platform.OS === 'web' ? 4 : 6, color: '#111827', fontSize: 14 }}
            />
            <TextInput
              value={Array.isArray(value) ? value.join(', ') : (value as string)}
              onChangeText={(text) => onChange && onChange(text)}
              placeholder={'內容'}
              multiline={label === '營業時間' || label === '特色推薦' || label === '交通方式'}
              numberOfLines={label === '營業時間' || label === '特色推薦' || label === '交通方式' ? 4 : 1}
              style={{ 
                flex: 1, 
                borderWidth: 1, 
                borderColor: '#E5E7EB', 
                borderRadius: 4, 
                paddingHorizontal: 8, 
                paddingVertical: Platform.OS === 'web' ? 6 : 8, 
                color: '#111827', 
                fontSize: 14,
                minHeight: (label === '營業時間' || label === '特色推薦' || label === '交通方式') ? 80 : undefined,
                textAlignVertical: 'top',
              }}
            />
            {onDelete && (
              <TouchableOpacity onPress={onDelete} style={{ paddingHorizontal: 8 }}>
                <Text style={{ color: '#EF4444', fontSize: 14 }}>刪除</Text>
              </TouchableOpacity>
            )}
          </View>
        </>
      ) : (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <Text style={{ width: '30%', color: '#6B7280', fontSize: 14 }}>{label}</Text>
          {hasValue ? (
            isStarRating ? (
              <View style={{ width: '70%' }}>
                {renderStars(value)}
              </View>
            ) : (
              <Text style={{ width: '70%', color: '#111827', fontSize: 14, fontWeight: '700' }}>{Array.isArray(value) ? value.join('、') : (value as string)}</Text>
            )
          ) : (
            <Text style={{ width: '70%', color: '#9CA3AF', fontSize: 14 }}>{placeholderText}</Text>
          )}
        </View>
      )}
    </View>
  );
};

export default MetaRow;
